import { Button } from "@components/atoms";
import React from "react";
import { useTheme } from "styled-components/native";
import styled, { DefaultTheme } from "styled-components/native";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import Routes from "@navigations/Routes";
import { navigate, push } from "@navigations/utils/RootNavigation";
import { useTranslation } from "react-i18next";
import { PDPGalleryProps } from "../types";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";
import { VDBIcons } from "@utils/VDBIcons";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useDispatch } from "react-redux";
import { BreadCrumbLabel } from "@components/molecules/Breadcrumbs/utils";
import { setBuilderProduct } from "@redux/slices/appStateSlice";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";
import { Platform } from "react-native";
import { css } from "styled-components/native";

type PDPFindMatchButtonProps = PDPGalleryProps & {
  buttonLabel: string;
  buttonIcon: string;
  testID?: string;
  dataTestId?: string;
};

const PDPFindMatchButton = ({
  enableMatchPare,
  isDiamond,
  isLabgrown,
  isGemstone,
  isJewelry,
  itemDetail,
  productColor,
  buttonIcon,
  buttonLabel,
  enterSecondFlow,
  productType,
  priceMode,
  testID,
  dataTestId,
}: PDPFindMatchButtonProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackButtonEvent } = useUserAnalytics();
  const productPrefix = useProductPrefix();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const dispatch = useDispatch();
  const { addRouteToHistory } = useNavigationHistory();
  const { setItemDetail } = useJewelryConfiguratorState();

  const handleMatchPair = () => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.MATCH_PAIR,
      getAnalyticsProductData(itemDetail)
    );
    if (enableJewelryBuilder) {
      const mName = Routes.FindMatchScreen;
      const mParams = {
        itemId: itemDetail?.id as number,
        priceMode,
        productPrefix: productType,
        productType: productType,
        breadCrumbLabel: BreadCrumbLabel.MATCHING_STONE,
      };
      dispatch(setBuilderProduct(productType));
      const xRoute = { name: mName, params: mParams };
      addRouteToHistory(xRoute);
      setItemDetail({});
      push(mName, mParams);
    } else {
      navigate(Routes.FindMatchScreen, {
        itemId: itemDetail?.id,
        productPrefix,
        productType: itemDetail?.type,
        enterSecondFlow: normalizeBoolean(enterSecondFlow),
      });
    }
  };

  const resolvedTestId = testID ?? "pdp.findMatch.button";
  const resolvedDataTestId = dataTestId ?? "pdp.findMatch.button";

  return (
    <ViewContainer>
      {(isDiamond || isLabgrown) && (
        <PairButtonView>
          <Button
            testID={resolvedTestId}
            dataTestId={resolvedDataTestId}
            type={ButtonType.tertiary}
            category={
              isDiamond
                ? ButtonCategory.diam
                : isGemstone
                ? ButtonCategory.gem
                : isJewelry
                ? ButtonCategory.jewelry
                : ButtonCategory.lab
            }
            showIcon={true}
            disabled={!enableMatchPare}
            iconSource={buttonIcon ? buttonIcon : VDBIcons.FIND_A_MATCH}
            hideText={true}
            showLeftText={true}
            textTypographyStyle={"H3-M"}
            iconColor={
              enableMatchPare
                ? (theme[productColor as keyof DefaultTheme] as string)
                : theme["core-04"]
            }
            title={t(
              buttonLabel ? buttonLabel : "productDetailPage.findMatch.text"
            )}
            onPress={handleMatchPair}
          />
        </PairButtonView>
      )}
    </ViewContainer>
  );
};

const ViewContainer = styled.View<any>`
  ${({ theme }) =>
    !(theme.isLargeScreen && Platform.OS === "web") &&
    css`
      flex: 1;
    `};
  padding-left: ${({ theme }) => (theme.isLargeScreen ? "0dp" : "3px")};
  padding-right: ${({ theme }) => (theme.isLargeScreen ? "0dp" : "3px")};
`;

const PairButtonView = styled.View`
  padding-top: ${({ theme }) => (theme.isLargeScreen ? "0dp" : "16px")};
  padding-bottom: ${({ theme }) => (theme.isLargeScreen ? "0dp" : "16px")};
`;

export default PDPFindMatchButton;
